import { useMemo } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router';
import { BrowserRouter } from 'react-router-dom';

import { UserRoleEnum } from 'APIPackage/AuthModule/enums/UserRoleEnum';
import { getUserAccountTypeIsInternal, getUserRoleSysName } from 'AuthPackage/MainModule/selectors/authSelectors';
import { AuthPage } from 'AuthPackage/MainModule/components/AuthPage';
import { PersonalAccountPage } from 'PersonalAccountPackage/MainModule/components/PersonalAcountPage';

import {
  adminRoutes,
  baseRoutes,
  ombudsmanRoutes,
  prosecutorRoutes,
  ROUTE_AUTH,
  ROUTE_LK,
  ROUTE_TEMPLATES_IMPORT,
} from '../routes/routes';
import { PrivateRoute } from './PrivateRoute';
import { getRoutes } from '../common/getRoutes';
import { IRootState } from '../interfaces/IRootState';
import { getLawInterface } from '../reducers/coreReducer';
import { LawInterfacesEnum } from '../enums/LawInterfacesEnum';

interface IRenderRoutesStoreProps {
  sysname: UserRoleEnum | undefined;
  isInternal: boolean;
  isErknm: boolean;
}

const RenderRoutesWithoutStore = ({ sysname, isInternal, isErknm }: IRenderRoutesStoreProps) => {
  const routesByRole = useMemo(() => getRoutes(isInternal, isErknm, sysname), [isErknm, isInternal, sysname]);

  const redirect: JSX.Element | null = useMemo(() => {
    let currentRoutes: string[] | [] = [];

    switch (sysname) {
      case UserRoleEnum.ADMIN:
        currentRoutes = adminRoutes;
        break;
      case UserRoleEnum.OMBUDSMAN:
        currentRoutes = ombudsmanRoutes;
        break;
      case UserRoleEnum.SUPERVISOR:
        if (isErknm && !isInternal) {
          currentRoutes = baseRoutes.filter(item => item !== ROUTE_TEMPLATES_IMPORT);
        } else {
          currentRoutes = baseRoutes;
        }
        break;
      case UserRoleEnum.SUPERIOR_PROSECUTOR:
      case UserRoleEnum.PROSECUTOR:
        currentRoutes = prosecutorRoutes;
        break;
      default:
    }
    if (sysname && !currentRoutes.some((item: string) => window.location.pathname.includes(item.replace('/:id', '')))) {
      return <Redirect to={ROUTE_LK} />;
    }

    return null;
  }, [isInternal, sysname, isErknm]);

  return (
    <BrowserRouter>
      <Switch>
        {routesByRole?.map(item => (
          <PrivateRoute
            key={item.path}
            path={item.path}
            component={item.element}
            checkOrganizations={!!item.checkOrganizations}
            exact={item.exact}
          />
        ))}
        <PrivateRoute key={ROUTE_LK} path={ROUTE_LK} component={PersonalAccountPage} checkOrganizations={false} />
        <Route exact key={ROUTE_AUTH} path={ROUTE_AUTH} component={AuthPage} />
        {redirect}
        <Redirect to={ROUTE_AUTH} />
      </Switch>
    </BrowserRouter>
  );
};

const mapStateToProps = (state: IRootState): IRenderRoutesStoreProps => ({
  sysname: getUserRoleSysName(state),
  isInternal: getUserAccountTypeIsInternal(state),
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
});

export const RenderRoutes = connect(mapStateToProps)(RenderRoutesWithoutStore);
